// OfferArtworkSection — admin upload widget for an offer's artwork.
//
// Lives inside admin-frontend/src/components/. Mirrors the AvatarCropper
// pattern (already used everywhere for profile photos) so the admin
// gets the same drag/zoom/crop ergonomics they're used to. The cropper
// outputs a 640×640 PNG with alpha preserved (canvas defaults to a
// transparent background; toBlob('image/png') keeps that), then we
// POST it to /api/admin/offers/:id/image. The backend re-validates,
// emits the 160×160 thumb variant, and updates offers.image_url.
//
// Three live preview tiles show how the artwork will look at hero
// (200px), card (96px), and thumb (56px) sizes against the same dark
// glass-morphism backdrop the player UI uses — admins see the actual
// player experience before saving.
//
// Create-flow note: this component requires a saved offerId, so the
// caller hides it until the offer exists. AdminOfferDetail renders an
// inline hint inside the Artwork Section header on the create flow
// instead.

import { useRef, useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import MediaGalleryModal, { MediaAsset } from '@baaligames/shared-ui/admin/components/MediaGalleryModal';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type Props = {
  offerId: number;
  /** Current artwork URL (null = no artwork uploaded yet). */
  imageUrl: string | null;
  /** Called whenever the artwork changes (upload or remove). Caller
   *  can refresh local state or refetch the offer. */
  onChange: (newUrl: string | null) => void;
};

export default function OfferArtworkSection({ offerId, imageUrl, onChange }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // 2026-05-20 — Media Library integration. When opened, the modal
  // lets the admin pick a previously-uploaded offer banner (no fresh
  // upload needed) OR upload a new one (which gets saved to the
  // library for future reuse). On pick we attach the chosen URL to
  // THIS offer via the existing setImageUrl endpoint.
  const [galleryOpen, setGalleryOpen] = useState(false);

  function pickFile() { fileRef.current?.click(); }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (f) setPickedFile(f);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    if (!/^image\/(png|webp)$/i.test(f.type)) {
      setErr('Drop a PNG or WebP file. JPEG cannot carry transparency.');
      return;
    }
    setErr(null);
    setPickedFile(f);
  }

  async function handleCropped(file: File) {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const fd = new FormData();
      fd.append('image', file, file.name || 'offer.png');
      const r = await adminApi.postForm<{ offer: { image_url: string | null } }>(
        `/admin/offers/${offerId}/image`, fd,
      );
      onChange(r.offer.image_url);
      setInfo('Artwork uploaded. New image is live.');
    } catch (e) {
      setErr((e as ApiError).message || 'Upload failed.');
    } finally {
      setBusy(false);
      setPickedFile(null);
    }
  }

  // 2026-05-20 — Library pick path. Hits the new PATCH endpoint that
  // attaches a pre-existing library URL to this offer without
  // re-uploading bytes. Skips the cropper entirely (the library
  // image was already cropped + normalised when it was first
  // uploaded).
  async function handleLibraryPick(asset: MediaAsset) {
    setGalleryOpen(false);
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await adminApi.patch<{ offer: { image_url: string | null } }>(
        `/admin/offers/${offerId}/image`,
        { url: asset.url },
      );
      onChange(r.offer.image_url);
      setInfo('Library artwork attached. New image is live.');
    } catch (e) {
      setErr((e as ApiError).message || 'Could not attach library artwork.');
    } finally { setBusy(false); }
  }

  async function removeArtwork() {
    if (!imageUrl) return;
    if (!confirm('Remove the offer artwork? The card will fall back to the type-icon.')) return;
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await adminApi.del<{ offer: { image_url: string | null } }>(
        `/admin/offers/${offerId}/image`,
      );
      onChange(r.offer.image_url);
      setInfo('Artwork removed.');
    } catch (e) {
      setErr((e as ApiError).message || 'Could not remove artwork.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/webp"
        className="hidden"
        onChange={onPick}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ─── Drop zone / current artwork ─── */}
        <div
          onClick={pickFile}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pickFile(); }}
          className={`relative rounded-2xl border-2 border-dashed
                      transition cursor-pointer
                      min-h-[260px] grid place-items-center p-4
                      ${dragOver
                        ? 'border-emerald-400 bg-emerald-500/5'
                        : 'ring-arena-200/60 dark:ring-arena-600/40 hover:border-emerald-500/60 dark:hover:border-emerald-400/40 bg-arena-50/40 dark:bg-arena-800/40'}`}
        >
          {imageUrl ? (
            // Show current artwork against the same dark glass backdrop
            // the player end uses, so the admin sees its actual look.
            <div className="relative w-full max-w-[220px] aspect-square rounded-2xl overflow-hidden
                            bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]
                            border ring-white/10 grid place-items-center">
              {/* Phase 16.5: cyan/brand halos removed. Single small
                  black drop-shadow on the artwork for soft contact
                  depth, light from top. */}
              <img
                src={imageUrl}
                alt="Current offer artwork"
                loading="lazy"
                decoding="async"
                className="relative w-[80%] h-[80%] object-contain
                           drop-shadow-[0_4px_6px_rgba(0,0,0,0.20)]"
              />
              <div className="absolute bottom-2 inset-x-2 text-center
                              text-[10px] uppercase tracking-[.2em] text-white/60 font-bold">
                Current artwork
              </div>
            </div>
          ) : (
            // Empty state — call to action
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center mb-3
                              bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-400/30">
                <Icon name="add_photo_alternate" size={28} filled />
              </div>
              <div className="font-bold text-sm text-arena-800 dark:text-neutral-100">
                Drop a PNG here or click to choose
              </div>
              <div className="text-[11px] text-arena-600 dark:text-neon-200/55 mt-1 max-w-[260px] mx-auto">
                Transparent background required · 100×100 minimum · 2 MB max ·
                we crop & compress to 640×640 PNG automatically.
              </div>
            </div>
          )}
        </div>

        {/* ─── Live preview tiles ─── */}
        <div className="rounded-2xl ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                        bg-arena-50/40 dark:bg-arena-800/40 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">
              Live preview
            </h4>
            <span className="text-[10px] text-arena-500 dark:text-neon-200/45">
              How players see it
            </span>
          </div>

          {imageUrl ? (
            <div className="space-y-3">
              <PreviewTile
                label="Hero (Rewards page top, dashboard FeaturedBanner)"
                size="hero"
                imageUrl={imageUrl}
              />
              <div className="grid grid-cols-2 gap-3">
                <PreviewTile
                  label="Card (offer grid)"
                  size="card"
                  imageUrl={imageUrl}
                />
                <PreviewTile
                  label="Thumb (admin lists)"
                  size="thumb"
                  // The thumb derives the smaller variant URL via the
                  // same naming convention the backend writes.
                  imageUrl={imageUrl.replace(/\.png$/, '.thumb.png')}
                />
              </div>
            </div>
          ) : (
            <div className="grid place-items-center min-h-[200px] text-center
                            text-arena-500 dark:text-neon-200/45">
              <div>
                <Icon name="image_search" size={32} className="mx-auto opacity-50 mb-2" />
                <p className="text-xs">Upload artwork to see live previews</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error / info strips */}
      {err && (
        <div className="rounded-lg bg-rose-500/10 ring-1 ring-inset ring-rose-400/40
                        text-rose-700 dark:text-rose-200 text-xs px-3 py-2">
          {err}
        </div>
      )}
      {info && (
        <div className="rounded-lg bg-emerald-500/10 ring-1 ring-inset ring-emerald-400/40
                        text-emerald-700 dark:text-emerald-200 text-xs px-3 py-2">
          {info}
        </div>
      )}

      {/* Action row */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={pickFile}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg
                     text-xs font-bold uppercase tracking-wider text-white
                     bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                     disabled:opacity-50 transition"
        >
          <Icon name={imageUrl ? 'cached' : 'upload'} size={14} />
          {imageUrl ? 'Replace artwork' : 'Upload artwork'}
        </button>
        {/* 2026-05-20 — Pick from Media Library. Opens a modal that
            lets the admin reuse any previously-uploaded offer banner
            via a single PATCH (no fresh upload). */}
        <button
          type="button"
          onClick={() => setGalleryOpen(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg
                     text-xs font-bold uppercase tracking-wider
                     text-emerald-700 dark:text-emerald-200
                     bg-emerald-50 hover:bg-emerald-100
                     dark:bg-emerald-500/10 dark:hover:bg-emerald-500/20
                     ring-1 ring-inset ring-emerald-400/40
                     disabled:opacity-50 transition"
        >
          <Icon name="photo_library" size={14} /> Pick from library
        </button>
        {imageUrl && (
          <button
            type="button"
            onClick={removeArtwork}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg
                       text-xs font-bold uppercase tracking-wider
                       text-rose-700 dark:text-rose-300
                       bg-rose-500/5 hover:bg-rose-500/15 ring-1 ring-inset ring-rose-400/30
                       disabled:opacity-50 transition"
          >
            <Icon name="delete" size={14} /> Remove
          </button>
        )}
        {busy && (
          <span className="inline-flex items-center gap-1.5 text-xs text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={14} className="animate-spin" />
            Working…
          </span>
        )}
      </div>

      {/* Cropper modal — opens whenever a file is picked. AvatarCropper
          handles the drag/zoom/crop interaction; we configure it for
          640×640 PNG output so transparency is preserved. The cropper
          posts the result to handleCropped(). */}
      {pickedFile && (
        <AvatarCropper
          title="Adjust offer artwork"
          initialFile={pickedFile}
          outputSize={640}
          outputFormat="image/png"
          outputFilename="offer.png"
          hint="Drag to reposition · pinch / scroll / use slider to zoom · final size 640×640 PNG with transparency preserved"
          onUpload={handleCropped}
          onClose={() => setPickedFile(null)}
        />
      )}

      {/* 2026-05-20 — Media Library modal. Filtered to offer banners. */}
      {galleryOpen && (
        <MediaGalleryModal
          intendedUse="offer_banner"
          title="Choose Offer Artwork"
          onPick={handleLibraryPick}
          onClose={() => setGalleryOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Preview tiles ────────────────────────────────────────────────

function PreviewTile({
  label, size, imageUrl,
}: {
  label: string;
  size: 'hero' | 'card' | 'thumb';
  imageUrl: string;
}) {
  // Each tile mirrors the dark glass backdrop the player surfaces use
  // so the admin sees actual on-platform appearance.
  // Phase 16.5: per-tile cyan/orange halos + heavy baked drop-shadows
  // removed. Single small black drop-shadow scaled to tile size.
  const dims = size === 'hero'
    ? { tile: 'h-32 sm:h-36', img: 'w-24 h-24 sm:w-28 sm:h-28', shadow: 'drop-shadow-[0_4px_6px_rgba(0,0,0,0.20)]' }
    : size === 'card'
    ? { tile: 'h-28',         img: 'w-16 h-16',                 shadow: 'drop-shadow-[0_3px_4px_rgba(0,0,0,0.20)]' }
    : { tile: 'h-20',         img: 'w-12 h-12',                 shadow: 'drop-shadow-[0_2px_3px_rgba(0,0,0,0.20)]' };

  return (
    <div>
      <div className={`relative rounded-xl overflow-hidden border ring-white/10
                       bg-gradient-to-br from-[#0a1626] via-[#0e1d33] to-[#1a0f08]
                       grid place-items-center ${dims.tile}`}>
        {/* Subtle vignette — kept; this is the card's own surface
            shading, not a halo on the artwork. */}
        <div className="absolute inset-0 shadow-[inset_0_2px_20px_rgba(255,255,255,0.04),inset_0_-20px_30px_rgba(0,0,0,0.4)] pointer-events-none" />
        {/* The image */}
        <img
          src={imageUrl}
          alt={label}
          loading="lazy"
          decoding="async"
          className={`relative ${dims.img} object-contain ${dims.shadow}`}
        />
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/55 truncate">
        {label}
      </div>
    </div>
  );
}
